/**
 * MarketingNavbar — for all public/pre-auth surfaces.
 * Structure: Logo (hard-left) | Links (centered) | Auth actions (hard-right)
 * Sets --hdr CSS var (56px) so drawers/panels align correctly.
 *
 * Items:
 *   Welcome → /#welcome (anchor)
 *   How It Works → /#how-it-works (anchor)
 *   Features → /#features (anchor)
 *   Discover → /discover (route)
 *   Sign In → /auth
 *   Get Started → /auth
 *
 * InviteIQ context: If iq_surface_katalog is present in localStorage,
 * shows a subtle "Invited by" badge to reinforce the curated experience.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

export const MarketingNavbar: React.FC = () => {
  const location = useLocation();
  const isLanding = location.pathname === '/' || location.pathname === '/landing';

  // InviteIQ surface detection — show subtle invited context
  const [inviteContext, setInviteContext] = React.useState<string | null>(null);
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('iq_surface_katalog');
      if (raw) {
        const parsed = JSON.parse(raw);
        setInviteContext(parsed?.handle || null);
      }
    } catch { /* noop */ }
  }, []);

  const handleAnchorClick = (e: React.MouseEvent, hash: string) => {
    if (isLanding) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className="border-b border-border/20 bg-background/80 backdrop-blur-sm sticky top-0 z-50"
      style={{ ['--hdr' as string]: '56px' }}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center h-14">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-2 no-underline shrink-0">
            <img src="/brand/katalag-logo.png" alt="Katalag" className="h-8 w-8 rounded-lg object-contain" />
            <span className="font-bold text-xl tracking-tight text-foreground">Katalag</span>
          </Link>

          {/* InviteIQ badge — subtle indicator of invited context */}
          {inviteContext && (
            <div className="hidden md:flex items-center ml-3 px-2.5 py-1 rounded-full bg-primary/8 border border-primary/15 text-xs text-primary font-medium">
              <span className="w-1.5 h-1.5 bg-primary rounded-full mr-1.5" />
              via @{inviteContext}
            </div>
          )}

          {/* Center: nav links */}
          <div className="hidden md:flex items-center justify-center gap-6 text-sm flex-1">
            {isLanding ? (
              <>
                <a href="#welcome" onClick={(e) => handleAnchorClick(e, 'welcome')} className="text-muted-foreground hover:text-foreground transition-colors">Welcome</a>
                <a href="#how-it-works" onClick={(e) => handleAnchorClick(e, 'how-it-works')} className="text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
                <a href="#features" onClick={(e) => handleAnchorClick(e, 'features')} className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
              </>
            ) : (
              <>
                <Link to="/#welcome" className="text-muted-foreground hover:text-foreground transition-colors">Welcome</Link>
                <Link to="/#how-it-works" className="text-muted-foreground hover:text-foreground transition-colors">How It Works</Link>
                <Link to="/#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</Link>
              </>
            )}
            <Link
              to="/discover"
              className={
                location.pathname === '/discover'
                  ? 'text-foreground font-medium transition-colors'
                  : 'text-muted-foreground hover:text-foreground transition-colors'
              }
            >
              Discover
            </Link>
            <a
              href="https://info.kloutforce.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              More Info <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* Right: Auth actions */}
          <div className="flex items-center gap-3 shrink-0 ml-auto md:ml-0">
            <Link
              to="/auth"
              className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            >
              Sign In
            </Link>
            <Link to="/auth" className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};
